import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get, qs } from '@/lib/api';

export interface BranchOption {
  id: string;
  name: string;
}

export interface DepartmentOption {
  id: string;
  branchId: string;
  name: string;
}

export interface BedRow {
  id: string;
  roomId: string;
  code: string;
  bedClass: string;
  chargeItemId: string | null;
  status: string;
  isActive: boolean;
}

export interface RoomRow {
  id: string;
  wardId: string;
  code: string;
  name: string | null;
  isActive: boolean;
  beds: BedRow[];
}

export interface WardRow {
  id: string;
  branchId: string;
  code: string;
  name: string;
  departmentId: string | null;
  defaultBedClass: string;
  nurseStationPhone: string | null;
  isActive: boolean;
  rooms: RoomRow[];
  department: { id: string; name: string } | null;
}

export interface BedCharge {
  id: string;
  code: string;
  name: string;
  basePriceMinor: number;
}

export const WARD_QUERY_KEYS = [['admin-wards'], ['wards'], ['wards-list'], ['bed-board']];

export function useRefreshWards() {
  const queryClient = useQueryClient();
  return () => {
    for (const queryKey of WARD_QUERY_KEYS) void queryClient.invalidateQueries({ queryKey });
  };
}

export const codeEntry = (value: string) => value.toUpperCase().replace(/\s/g, '');

export function useWardRecord(wardId: string, branchId: string) {
  return useQuery({
    queryKey: ['admin-wards', 'lookup', branchId, wardId],
    queryFn: async () => {
      const rows = await get<WardRow[]>(`/ipd/wards${qs({ branchId: branchId || undefined, includeInactive: 1, limit: 500 })}`);
      return rows.find((ward) => ward.id === wardId) ?? null;
    },
    enabled: Boolean(wardId),
  });
}

export function useBedCharges(enabled: boolean) {
  return useQuery({
    queryKey: ['charge-items', 'bed-category'],
    queryFn: async () => (await get<{ data: BedCharge[] }>('/charge-items?category=bed&limit=500')).data,
    enabled,
  });
}

export function useBranchOptions() {
  return useQuery({
    queryKey: ['branches'],
    queryFn: () => get<BranchOption[]>('/org/branches'),
  });
}
